import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import logo from "../images/logoo.png";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: "easeOut" }
};

const achievements = [
  { number: "50+", label: "Projects Delivered", icon: "🚀", description: "Successfully completed across industries" },
  { number: "98%", label: "Client Satisfaction", icon: "⭐", description: "Proven track record of excellence" },
  { number: "3+", label: "Years Experience", icon: "💼", description: "Established technology expertise" },
  { number: "24/7", label: "Support Available", icon: "🔧", description: "Round-the-clock assistance" }
];

const services = [
  {
    title: "Enterprise Web Development",
    description: "Scalable web applications with modern architecture and enterprise-grade security",
    icon: "🌐",
    features: ["React & Node.js", "Cloud Infrastructure", "API Development", "Database Design"]
  },
  {
    title: "Mobile App Development",
    description: "Native and cross-platform mobile applications for iOS and Android platforms",
    icon: "📱",
    features: ["Flutter & React Native", "iOS Development", "Android Development", "App Store Deployment"]
  },
  {
    title: "AI & Machine Learning",
    description: "Intelligent automation solutions and custom AI models for business optimization",
    icon: "🤖",
    features: ["Machine Learning Models", "Data Analytics", "Automation Systems", "AI Integration"]
  },
  {
    title: "Technical Documentation",
    description: "Professional documentation services and technical writing for all project types",
    icon: "📚",
    features: ["Technical Writing", "API Documentation", "User Manuals", "Project Reports"]
  }
];

const processSteps = [
  { step: "01", title: "Discovery", description: "Understanding your business requirements and technical specifications", icon: "🔍" },
  { step: "02", title: "Planning", description: "Creating detailed project roadmap and resource allocation", icon: "📋" },
  { step: "03", title: "Development", description: "Agile development with regular updates and quality assurance", icon: "⚙️" },
  { step: "04", title: "Deployment", description: "Seamless launch with ongoing support and maintenance", icon: "🚀" }
];

export default function Welcome() {
  return (
    <div className="min-h-screen bg-[#020617] text-slate-100 selection:bg-blue-500/10 overflow-x-hidden font-inter">
      {/* Background Graphics */}
      <div className="fixed inset-0 bg-grid opacity-10 pointer-events-none" />
      <div className="fixed top-0 left-0 w-[1000px] h-[1000px] bg-blue-500/5 blur-[150px] rounded-full -ml-96 -mt-96 animate-blob" />
      <div className="fixed bottom-0 right-0 w-[800px] h-[800px] bg-indigo-500/5 blur-[150px] rounded-full -mr-48 -mb-48 animate-blob delay-2000" />
      <div className="fixed inset-0 bg-mesh opacity-20 pointer-events-none" />

      {/* Hero Section */}
      <section className="relative pt-48 pb-32 px-6 overflow-hidden">
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="flex flex-col items-center text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, type: "spring", bounce: 0.4 }}
              className="mb-14 relative"
            >
              <div className="absolute -inset-8 bg-blue-500/10 blur-3xl rounded-full" />
              <img
                src={logo}
                alt="LAB Developers"
                className="w-32 h-32 object-contain relative z-10 p-4 bg-white/5 backdrop-blur-3xl rounded-[2.5rem] border border-white/10 shadow-[0_30px_60px_rgba(0,0,0,0.5)]"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h1 className="text-5xl md:text-7xl lg:text-[9rem] font-black mb-10 tracking-tighter leading-[0.8]">
                <span className="text-white">LAB</span><br />
                <span className="text-gradient">Developers.</span>
              </h1>
              <p className="text-lg md:text-xl lg:text-2xl text-slate-400 max-w-4xl mx-auto mb-16 font-medium leading-relaxed lowercase tracking-tight px-4">
                we bridge the gap between human ambition and high-fidelity computing. surgical software for the enterprise of tomorrow.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex flex-wrap justify-center gap-8"
            >
              <Link
                to="/home"
                className="group px-14 py-6 bg-slate-900 text-white font-black rounded-3xl hover:bg-slate-800 transition-all duration-500 shadow-2xl flex items-center gap-4 group"
              >
                <span className="uppercase tracking-[0.2em] text-[11px]">ENTER ECOSYSTEM</span>
                <span className="text-xl transition-transform group-hover:translate-x-1.5">→</span>
              </Link>
              <Link
                to="/contact"
                className="px-14 py-6 bg-slate-900/40 border border-white/10 text-white font-black rounded-3xl hover:bg-[#020617] transition-all duration-500 shadow-sm flex items-center gap-4 uppercase tracking-[0.2em] text-[11px]"
              >
                INITIATE PROJECT
              </Link>
            </motion.div>

            {/* Quick Stats */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 1 }}
              className="mt-40 grid grid-cols-2 lg:grid-cols-4 gap-16 lg:gap-32 px-12 py-12 bg-white/5 backdrop-blur-xl rounded-[4rem] border border-white/10 shadow-2xl"
            >
              {achievements.map((item, i) => (
                <div key={i} className="text-center group">
                  <div className="text-5xl font-black text-white mb-2 group-hover:text-blue-600 transition-all tracking-tighter group-hover:scale-110 duration-500">{item.number}</div>
                  <div className="text-[10px] uppercase tracking-[0.4em] text-slate-400 font-black leading-none">{item.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
       <section className="py-40 px-6 relative z-10 bg-[#020617] border-b border-white/5 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-10" />
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-12">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                 className="group glass-card p-10 md:p-16 rounded-[3rem] md:rounded-[4.5rem] border border-white/10 hover:bg-white/5 transition-all duration-700 hover:shadow-2xl hover:translate-y-[-10px] glow-border"
              >
                <div className="w-24 h-24 bg-slate-900/40 text-5xl mb-12 flex items-center justify-center rounded-3xl border border-white/5 group-hover:scale-110 group-hover:rotate-6 transition-all duration-700 shadow-sm">
                  {service.icon}
                </div>
                <h3 className="text-4xl font-black text-white mb-8 tracking-tighter uppercase leading-none">{service.title}</h3>
                <p className="text-slate-400 mb-12 text-lg leading-relaxed font-medium lowercase tracking-tight">{service.description}</p>
                <div className="flex flex-wrap gap-3 pt-12 border-t border-white/5">
                  {service.features.map((f, i) => (
                    <span key={i} className="px-5 py-2.5 bg-[#020617] rounded-full text-[10px] font-black text-slate-400 uppercase tracking-widest border border-white/5 group-hover:text-blue-600 group-hover:border-blue-100 transition-colors">
                      {f}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-40 px-6 bg-[#020617] text-slate-100 relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-mesh opacity-30 pointer-events-none" />
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="text-center mb-36">
            <h2 className="text-6xl md:text-8xl font-black text-white mb-8 tracking-tighter uppercase leading-none">Protocol <br />Flow.</h2>
            <p className="text-slate-400 max-w-2xl mx-auto font-medium lowercase tracking-tight text-xl">precision-engineered architectural lifecycle.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {processSteps.map((step, i) => (
              <div key={i} className="relative group p-10 rounded-[3rem] hover:bg-white/5 transition-all duration-500 border border-white/10 shadow-sm hover:shadow-2xl">
                <div className="text-[12rem] font-black text-slate-100 absolute -top-24 left-10 group-hover:text-blue-500/5 transition-all pointer-events-none font-outfit duration-700">
                  {step.step}
                </div>
                <div className="relative z-10">
                  <div className="text-6xl mb-10 transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-700 h-16 w-16 flex items-center justify-center bg-white/5 rounded-2xl border border-white/10 shadow-sm">{step.icon}</div>
                  <h4 className="text-2xl font-black mb-6 uppercase tracking-tight text-white">{step.title}</h4>
                  <p className="text-slate-400 text-sm font-medium leading-relaxed lowercase">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-24 px-6 relative z-10 bg-[#020617]">
        <div className="absolute inset-0 bg-grid opacity-5 pointer-events-none" />
        <div className="container mx-auto max-w-5xl relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="glass-card p-12 md:p-16 rounded-[3.5rem] text-center border border-white/10 shadow-[0_60px_120px_rgba(0,0,0,0.5)] relative overflow-hidden"
          >
            <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-indigo-500/10 blur-[100px] rounded-full" />
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-white mb-8 tracking-tighter uppercase leading-none">Develop <br /><span className="text-gradient">Immensity.</span></h2>
            <p className="text-base md:text-xl lg:text-2xl text-slate-400 mb-16 max-w-3xl mx-auto font-medium lowercase tracking-tight px-4">
              we are currently architecting the future of technical infrastructure. connect with our engineering lead today.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-8">
              <Link
                to="/home"
                className="px-16 py-7 bg-blue-600 text-white font-black rounded-3xl hover:bg-blue-700 transition-all shadow-2xl hover:-translate-y-1.5 uppercase tracking-widest text-xs"
              >
                OPEN CONSOLE
              </Link>
              <Link
                to="/contact"
                className="px-16 py-7 bg-slate-900/40 border border-white/10 text-white font-black rounded-3xl hover:bg-[#020617] transition-all hover:-translate-y-1.5 uppercase tracking-widest text-xs"
              >
                START SPRINT
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
